import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { gsap } from '../lib/gsapSetup';
import { useReducedMotion } from '../hooks/useMotionPrefs';

// Full energy-infrastructure hero: 3 receding corridors of transmission towers with
// 3-phase catenary lines, a substation, solar fields and rotating wind turbines.
// The whole plant constructs itself on load; energy pulses travel the front lines.
const ROWS = [
  { z: -6, h: 9, xs: [-16, -8, 0, 8, 16], tone: 0x5c5c5c, lineOpacity: 0.95 },
  { z: -14, h: 8, xs: [-18, -9, 0, 9, 18], tone: 0x494949, lineOpacity: 0.6 },
  { z: -23, h: 7, xs: [-14, -4.5, 4.5, 14], tone: 0x3a3a3a, lineOpacity: 0.38 },
];

const TURBINES = [
  { x: -24, z: -19, s: 1 },
  { x: 22, z: -26, s: 1.25 },
  { x: 27, z: -13, s: 0.85 },
];

const UP = new THREE.Vector3(0, 1, 0);

// Real lattice transmission pylon: tapered cross-braced legs, latticed body,
// truss crossarms and a ground-wire peak — merged into a single mesh per tower.
function buildLatticeTower(h, tone) {
  const geos = [];
  const add = (a, b, th = 0.05) => {
    const dir = b.clone().sub(a);
    const len = dir.length();
    const g = new THREE.BoxGeometry(th, len, th);
    g.translate(0, len / 2, 0);
    const q = new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize());
    g.applyMatrix4(new THREE.Matrix4().compose(a, q, new THREE.Vector3(1, 1, 1)));
    geos.push(g);
  };
  const V = (x, y, z) => new THREE.Vector3(x, y, z);
  const lerp = (a, b, t) => a + (b - a) * t;
  const corners = (hw, y) => [V(-hw, y, -hw), V(hw, y, -hw), V(hw, y, hw), V(-hw, y, hw)];

  const bw = h * 0.15; // base half-width
  const ww = h * 0.05; // waist half-width
  const waistY = h * 0.62;
  const bodyTopY = h * 0.8;
  const tw = h * 0.03;
  const peakY = h * 1.02;
  const armLowY = h * 0.84;
  const armHighY = h * 0.92;
  const al = h * 0.17; // arm half-length

  // tapered legs with rings and zigzag cross-bracing
  const segLevels = [0, 0.22, 0.42, 0.62];
  for (let s = 0; s < segLevels.length - 1; s++) {
    const y0 = segLevels[s] * h;
    const y1 = segLevels[s + 1] * h;
    const w0 = lerp(bw, ww, segLevels[s] / 0.62);
    const w1 = lerp(bw, ww, segLevels[s + 1] / 0.62);
    const c0 = corners(w0, y0);
    const c1 = corners(w1, y1);
    for (let c = 0; c < 4; c++) {
      const next = (c + 1) % 4;
      add(c0[c], c1[c], 0.065); // leg
      add(c1[c], c1[next], 0.042); // ring
      if (s % 2 === 0) add(c0[c], c1[next], 0.036);
      else add(c0[next], c1[c], 0.036);
    }
  }

  // latticed upper body + peak struts
  const wc = corners(ww, waistY);
  const tc = corners(tw, bodyTopY);
  for (let c = 0; c < 4; c++) {
    add(wc[c], tc[c], 0.055);
    add(tc[c], tc[(c + 1) % 4], 0.038);
    add(wc[c], tc[(c + 1) % 4], 0.034);
    add(tc[c], V(0, peakY, 0), 0.045);
  }

  // two truss crossarms
  [armLowY, armHighY].forEach((ay) => {
    add(V(-al, ay, -0.14), V(al, ay, -0.14), 0.05);
    add(V(-al, ay, 0.14), V(al, ay, 0.14), 0.05);
    const steps = 4;
    for (let k = 0; k <= steps; k++) {
      const ax = lerp(-al, al, k / steps);
      add(V(ax, ay, -0.14), V(ax, ay, 0.14), 0.034);
      if (k < steps) {
        const nx = lerp(-al, al, (k + 1) / steps);
        if (k % 2 === 0) add(V(ax, ay, -0.14), V(nx, ay, 0.14), 0.028);
        else add(V(ax, ay, 0.14), V(nx, ay, -0.14), 0.028);
      }
    }
    add(V(0, ay, 0), V(0, ay - h * 0.07, 0), 0.055);
  });

  const group = new THREE.Group();
  group.add(new THREE.Mesh(mergeGeometries(geos, false), new THREE.MeshBasicMaterial({ color: tone })));
  // 3-phase line attach points (local): low-arm tips + high-arm centre
  const attach = [V(-al * 0.9, armLowY, 0), V(al * 0.9, armLowY, 0), V(0, armHighY, 0)];
  return { group, attach };
}

export default function HeroScene() {
  const mountRef = useRef(null);
  const [ready, setReady] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const mount = mountRef.current;
    const w = mount.offsetWidth;
    const h = mount.offsetHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.FogExp2(0x000000, 0.021);

    const camera = new THREE.PerspectiveCamera(w < h ? 74 : 55, w / h, 0.1, 220);
    camera.position.set(0, 2.6, 28);

    // Flythrough path — dives into the corridor between the towers
    const camCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 2.6, 30),
      new THREE.Vector3(1.2, 2.9, 16),
      new THREE.Vector3(4.0, 3.2, 2),
      new THREE.Vector3(4.4, 3.9, -7),
      new THREE.Vector3(6.2, 5.2, -0.5),
    ]);
    const lookTarget = new THREE.Vector3();

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.6));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new UnrealBloomPass(new THREE.Vector2(w, h), 0.5, 0.35, 0.35));
    composer.addPass(new OutputPass());

    // Ground grid fading into fog
    scene.add(new THREE.GridHelper(260, 100, 0x14243a, 0x101010));

    // Ambient dust
    const dustCount = window.innerWidth < 768 ? 60 : 130;
    const dustPos = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 80;
      dustPos[i * 3 + 1] = Math.random() * 15;
      dustPos[i * 3 + 2] = -Math.random() * 50 + 10;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dust = new THREE.Points(
      dustGeo,
      new THREE.PointsMaterial({ color: 0x47a0d9, size: 0.09, transparent: true, opacity: 0.6, depthWrite: false })
    );
    scene.add(dust);

    // Transmission corridors — towers + 3-phase catenary lines per span
    const towerGroups = [];
    const lines = [];
    const glows = [];
    ROWS.forEach((row, ri) => {
      const tops = [];
      row.xs.forEach((x, i) => {
        const built = buildLatticeTower(row.h, row.tone);
        const g = built.group;
        g.position.set(x, 0, row.z);
        g.scale.y = reduced ? 1 : 0.001;
        scene.add(g);
        towerGroups.push({ g, ri, i });
        tops.push(built.attach.map((p) => p.clone().add(new THREE.Vector3(x, 0, row.z))));

        if (ri <= 1) {
          const core = new THREE.Mesh(
            new THREE.SphereGeometry(0.19, 12, 12),
            new THREE.MeshBasicMaterial({ color: 0x47a0d9, transparent: true, opacity: reduced ? 1 : 0 })
          );
          core.position.set(x, row.h * 1.02 + 0.3, row.z);
          const halo = new THREE.Mesh(
            new THREE.SphereGeometry(0.5, 12, 12),
            new THREE.MeshBasicMaterial({
              color: 0x47a0d9,
              transparent: true,
              opacity: reduced ? 0.22 : 0,
              blending: THREE.AdditiveBlending,
              depthWrite: false,
            })
          );
          halo.position.set(x, row.h * 1.02 + 0.3, row.z);
          scene.add(core);
          scene.add(halo);
          glows.push({ core, halo });
        }
      });

      for (let i = 0; i < tops.length - 1; i++) {
        [0, 1, 2].forEach((phase) => {
          const a = tops[i][phase].clone();
          const b = tops[i + 1][phase].clone();
          const mid = a.clone().add(b).multiplyScalar(0.5);
          mid.y = Math.min(a.y, b.y) - 1.5;
          const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
          const pts = curve.getPoints(40);
          const geo = new THREE.BufferGeometry().setFromPoints(pts);
          const mat = new THREE.LineBasicMaterial({ color: 0x47a0d9, transparent: true, opacity: 0 });
          const line = new THREE.Line(geo, mat);
          geo.setDrawRange(0, reduced ? pts.length : 0);
          if (reduced) mat.opacity = row.lineOpacity;
          scene.add(line);
          lines.push({ geo, mat, count: pts.length, curve, row: ri, span: i, phase, targetOpacity: row.lineOpacity });
        });
      }
    });

    // Energy pulses travelling the front corridor's centre phase
    const pulses = lines
      .filter((l) => l.row <= 1 && l.phase === 1)
      .map((l, i) => {
        const m = new THREE.Mesh(
          new THREE.SphereGeometry(0.11, 8, 8),
          new THREE.MeshBasicMaterial({
            color: 0x8fc9f2,
            transparent: true,
            opacity: reduced ? 0.95 : 0,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        scene.add(m);
        return { mesh: m, curve: l.curve, offset: i * 0.27 };
      });

    // Substation — wide platform, bushing row, transformer block
    const subTips = [];
    const substation = new THREE.Group();
    const subMat = new THREE.MeshBasicMaterial({ color: 0x333333 });
    const baseGeo = new THREE.BoxGeometry(6.2, 0.5, 3.0);
    baseGeo.translate(0, 0.25, 0);
    substation.add(new THREE.Mesh(baseGeo, subMat));
    const trafoGeo = new THREE.BoxGeometry(1.7, 1.3, 1.2);
    trafoGeo.translate(0, 1.15, 0);
    const trafo = new THREE.Mesh(trafoGeo, new THREE.MeshBasicMaterial({ color: 0x2b2b2b }));
    trafo.position.x = -1.6;
    substation.add(trafo);
    [-0.4, 0.6, 1.6, 2.6].forEach((bx) => {
      const busGeo = new THREE.CylinderGeometry(0.08, 0.1, 1.5, 8);
      busGeo.translate(0, 1.25, 0);
      const bus = new THREE.Mesh(busGeo, new THREE.MeshBasicMaterial({ color: 0x4a4a4a }));
      bus.position.x = bx;
      substation.add(bus);
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0x47a0d9, transparent: true, opacity: reduced ? 1 : 0 })
      );
      tip.position.set(bx, 2.15, 0);
      substation.add(tip);
      subTips.push(tip);
    });
    substation.position.set(0, 0, -3.4);
    substation.scale.y = reduced ? 1 : 0.001;
    scene.add(substation);

    // Solar fields flanking the corridors
    const solarFields = [];
    const makeField = (originX, originZ, rows, cols) => {
      const mat2 = new THREE.MeshBasicMaterial({ color: 0x1a2634, transparent: true, opacity: reduced ? 1 : 0 });
      const field = new THREE.Group();
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const p = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.05, 0.7), mat2);
          p.position.set(c * 1.35, 0.38, r * 1.1);
          p.rotation.x = -0.42;
          field.add(p);
        }
      }
      field.position.set(originX, reduced ? 0 : -0.6, originZ);
      scene.add(field);
      solarFields.push({ group: field, mat: mat2 });
    };
    makeField(-21, -9, 3, 5);
    makeField(14, -9, 3, 5);

    // Wind turbines on the horizon — blades rotate continuously
    const turbines = [];
    TURBINES.forEach(({ x, z, s }) => {
      const g = new THREE.Group();
      const poleGeo = new THREE.CylinderGeometry(0.14 * s, 0.24 * s, 9 * s, 8);
      poleGeo.translate(0, 4.5 * s, 0);
      g.add(new THREE.Mesh(poleGeo, new THREE.MeshBasicMaterial({ color: 0x626262 })));
      const hub = new THREE.Group();
      hub.position.set(0, 9 * s, 0.25);
      for (let b = 0; b < 3; b++) {
        const bladeGeo = new THREE.BoxGeometry(0.16 * s, 3.4 * s, 0.06);
        bladeGeo.translate(0, 1.7 * s, 0);
        const blade = new THREE.Mesh(bladeGeo, new THREE.MeshBasicMaterial({ color: 0x6e6e6e }));
        blade.rotation.z = (b * Math.PI * 2) / 3;
        hub.add(blade);
      }
      g.add(hub);
      g.position.set(x, 0, z);
      g.scale.y = reduced ? 1 : 0.001;
      scene.add(g);
      turbines.push({ group: g, hub, speed: 0.55 + Math.random() * 0.35 });
    });

    // Build sequence — the plant constructs itself, front to back
    const tweens = [];
    if (!reduced) {
      towerGroups.forEach(({ g, ri, i }) => {
        tweens.push(gsap.to(g.scale, { y: 1, duration: 1.5, delay: 0.8 + ri * 0.55 + i * 0.22, ease: 'power3.out' }));
      });
      tweens.push(
        gsap.to(substation.scale, { y: 1, duration: 1.3, delay: 1.3, ease: 'power3.out' }),
        ...turbines.map((tb, i) => gsap.to(tb.group.scale, { y: 1, duration: 1.6, delay: 1.1 + i * 0.4, ease: 'power3.out' })),
        ...solarFields.map((f, i) => gsap.to(f.group.position, { y: 0, duration: 1.4, delay: 1.9 + i * 0.35, ease: 'power3.out' })),
        ...solarFields.map((f, i) => gsap.to(f.mat, { opacity: 1, duration: 1.2, delay: 1.9 + i * 0.35 }))
      );
      lines.forEach((l) => {
        const d = 2.6 + l.row * 0.5 + l.span * 0.28 + l.phase * 0.07;
        const proxy = { n: 0 };
        tweens.push(
          gsap.to(proxy, {
            n: l.count,
            duration: 1.3,
            delay: d,
            ease: 'power2.inOut',
            onUpdate: () => l.geo.setDrawRange(0, Math.floor(proxy.n)),
          }),
          gsap.to(l.mat, { opacity: l.targetOpacity, duration: 1.1, delay: d })
        );
      });
      glows.forEach((g, i) => {
        tweens.push(
          gsap.to(g.core.material, { opacity: 1, duration: 0.8, delay: 3.8 + i * 0.3 }),
          gsap.to(g.halo.material, { opacity: 0.22, duration: 1.2, delay: 3.8 + i * 0.3 })
        );
      });
      tweens.push(
        ...subTips.map((tip, i) => gsap.to(tip.material, { opacity: 1, duration: 0.8, delay: 4.0 + i * 0.2 })),
        ...pulses.map((p, i) => gsap.to(p.mesh.material, { opacity: 0.95, duration: 1, delay: 4.8 + i * 0.3 }))
      );
    }

    // Subtle mouse parallax (desktop)
    let mouseX = 0;
    let camX = 0;
    const onMouse = (e) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
    };
    if (window.matchMedia('(pointer: fine)').matches && !reduced) {
      window.addEventListener('mousemove', onMouse, { passive: true });
    }

    let visible = true;
    const io = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), { threshold: 0.02 });
    io.observe(mount);

    const clock = new THREE.Clock();
    let raf;
    let firstFrame = true;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      const t = clock.getElapsedTime();
      if (!reduced) {
        const prog = Math.min(1, t / 13);
        const e = prog * prog * (3 - 2 * prog);
        camera.position.copy(camCurve.getPoint(e));
        camX += (mouseX * 0.9 - camX) * 0.03;
        camera.position.x += camX;
        glows.forEach((g, i) => {
          const pulse = 1 + 0.16 * Math.sin(t * 2.2 + i * 1.4);
          g.core.scale.setScalar(pulse);
          g.halo.scale.setScalar(pulse);
        });
        turbines.forEach((tb) => {
          tb.hub.rotation.z = t * tb.speed;
        });
        pulses.forEach((p) => {
          const u = (t * 0.11 + p.offset) % 1;
          p.mesh.position.copy(p.curve.getPoint(u));
        });
        dust.rotation.y = t * 0.008;
      }
      if (!reduced) {
        lookTarget.set(0, 4.2, camera.position.z - 16);
        camera.lookAt(lookTarget);
      } else {
        camera.lookAt(0, 3.6, -14);
      }
      composer.render();
      if (firstFrame) {
        firstFrame = false;
        setReady(true);
      }
    };
    tick();

    const onResize = () => {
      const nw = mount.offsetWidth;
      const nh = mount.offsetHeight;
      camera.aspect = nw / nh;
      camera.fov = nw < nh ? 74 : 55;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
      composer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouse);
      tweens.forEach((tw) => tw.kill());
      composer.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, [reduced]);

  return (
    <div
      ref={mountRef}
      data-testid="hero-scene"
      className={`hero-video absolute inset-0 will-change-transform transition-opacity duration-[1500ms] ${
        ready ? 'opacity-100' : 'opacity-0'
      }`}
    />
  );
}
